import {
  Button,
  CalendarPopover,
  Icon,
  parseDate,
  type DateValue,
} from "@ttc3k/trekker";
import { format, addMonths } from "date-fns";
import type { Locale } from "date-fns";
import { Calendar } from "iconoir-react";
import { useMemo, useState } from "react";
import { Box } from "styled-system/jsx";
import { parseCalendarValueAsString } from "shared/utils";
import {
  PROMOTION_DATE_FORMAT,
  parsePromotionDate,
} from "shared/utils/promotionHelpers";

type PromotionDateFieldProps = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  min?: string;
  max?: string;
  nonSelectableDates?: string[];
  locale?: Locale;
  disabled?: boolean;
  /** Lowers the trigger below a sibling's open calendar. */
  reduceZIndex?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Single-date picker shaped like a text field (40px, white, bordered), as in
 * the promo-form design. Built on Trekker's CalendarPopover.
 */
export const PromotionDateField = ({
  value,
  onValueChange,
  placeholder,
  min,
  max,
  nonSelectableDates = [],
  locale,
  disabled,
  reduceZIndex = false,
  onOpenChange,
}: PromotionDateFieldProps) => {
  const [isOpen, setIsOpenState] = useState(false);
  const setIsOpen = (open: boolean) => {
    setIsOpenState(open);
    onOpenChange?.(open);
  };

  const formatted = useMemo(
    () =>
      value ? format(parsePromotionDate(value), "MMM d, yyyy", { locale }) : "",
    [value, locale],
  );
  const maxDate =
    max ?? format(addMonths(new Date(), 24), PROMOTION_DATE_FORMAT);

  return (
    <Box width="full" position="relative">
      {isOpen && (
        <Box
          position="fixed"
          inset="0"
          zIndex="10"
          onClick={() => setIsOpen(false)}
        />
      )}
      <CalendarPopover
        selectionMode="single"
        tabIndex={0}
        value={value ? [parseDate(value)] : []}
        onValueChange={({ valueAsString }: { valueAsString: string[] }) => {
          const next = parseCalendarValueAsString(valueAsString)[0];
          if (next) onValueChange(next);
          setIsOpen(false);
        }}
        isDateUnavailable={(date: DateValue) => {
          const iso = date.toString().substring(0, 10);
          return nonSelectableDates.includes(iso) && iso !== value;
        }}
        min={min ? parseDate(min) : undefined}
        max={parseDate(maxDate)}
        disablePastDates={false}
        open={isOpen}
        noPortal
        outline="none"
        width="full"
        // Closed: stack with the body (under the pinned footer); open: above everything
        zIndex={isOpen ? 30 : reduceZIndex ? 0 : "auto"}
        Trigger={
          <Button
            visual="primary"
            mode="light"
            type="button"
            width="full"
            h="40px"
            px="150"
            py="100"
            borderRadius="100"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            fontWeight="normal"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span
              style={{
                minHeight: "24px",
                color: formatted ? undefined : "var(--wa-colors-ink-500)",
              }}
            >
              {formatted || placeholder}
            </span>
            <Icon
              Element={Calendar}
              color="icon.light"
              width="16px"
              height="16px"
            />
          </Button>
        }
      />
    </Box>
  );
};
